import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from '../../i18n/i18n';
import { useNavigate } from 'react-router-dom';
import {
  SquareChevronLeft,
  Plane,
  Hotel,
  Mountain,
  Car,
  Calendar,
  User,
  Building2,
  ArrowBigRight,
  CheckCircle,
  XCircle,
  DollarSign,
  Mars,
  Venus,
  Briefcase,
  Shield,
  Package,
  Download,
  FileSpreadsheet,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import api from '../../api/axios';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { HotelSection, FlightSection, ActivitySection, TransferSection } from './BookingType';

const BookingDetailPage = ({ lang }) => {
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [cancellationPolicies, setCancellationPolicies] = useState([]);
  const [policies, setPolicies] = useState([]);
  const { t } = useTranslation(lang);
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);
  const booking_url = process.env.REACT_APP_API_BOOKING_URL;

  const fetchBookingDetail = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`${booking_url}/${id}`);

      const transformedData = transformBookingData(data.result);
      setBooking(transformedData);

      setCancellationPolicies(data.result.cancellationPolicies || []);
      setPolicies(data.result.rateComment || []);

    } catch (error) {
      console.error('Erreur lors du chargement des détails:', error);
      toast.error(t('errorLoadingBooking'));
    } finally {
      setLoading(false);
    }
  };

  const transformBookingData = (newBooking) => {
    return {
      // Basic booking info
      booking_number: newBooking.bookingNumber,
      bookingNumber: newBooking.bookingNumber,
      status: newBooking.status,
      payment_status: newBooking.paymentStatus,
      service: newBooking.bookingType,
      amount_total: newBooking.amountTotal,
      currency: newBooking.currency,
      provider_amount_total: newBooking.providerAmountTotal,
      provider_currency: newBooking.providerCurrency,
      checkin: newBooking.checkin,
      checkout: newBooking.checkout,
      duration: newBooking.duration,
      created_at: newBooking.confirmedDate,
      cancelled_at: newBooking.cancelledDate,
      payment_date_limit_at: newBooking.paymentDateLimit,
      date_limit_cancellation: newBooking.dateLimitCcancellation,

      // Client information
      holder: newBooking.holder,
      customer: newBooking.customer,
      customer_id: newBooking.customer?.id,
      customer_account_id: newBooking.customer?.id,

      // Provider information
      provider_id: newBooking.provider?.id,
      provider: newBooking.provider,

      // Product information
      product_id: newBooking.product?.id,
      product: newBooking.product,
      country: newBooking.country,
      destination: newBooking.product?.city,
      country_id: newBooking.country?.id,
      destination_id: newBooking.product?.city?.id,

      // Flight specific (if applicable)
      booking_flight: newBooking.flight ? {
        trip: newBooking.flight.trip,
        adult: newBooking.flight.adult,
        child: newBooking.flight.child,
        infant: newBooking.flight.infant,
        airline: newBooking.flight.airline,
        itineraries: newBooking.flight.itinerary?.map(it => ({
          departure: it.departure,
          arrival: it.arrival,
          segments: it.segments,
          duration: it.segments?.[0]?.duration
        })),
      } : null,

      booking_transfer: newBooking.transfer ? {
        type: newBooking.transfer.type,
        adult: newBooking.transfer.adult,
        child: newBooking.transfer.child,
        itineraries: newBooking.transfer.itinerary?.map(it => ({
          origin: it.origin,
          destination: it.destination,
          vehicle: it.vehicle,
          transferDate: it.transferDate,
          transferTime: it.transferTime,
          travelType: it.travelType,
          travelNumber: it.travelNumber
        }))
      } : null,

      booking_activity: newBooking.activity ? {
        days: newBooking.activity.days,
        adult: newBooking.activity.adult,
        child: newBooking.activity.child,
        baby: newBooking.activity.baby,
        senior: newBooking.activity.senior,
        amount: newBooking.activity.amount,
        prices: newBooking.activity.prices
      } : null,

      booking_hotel: newBooking.hotel ? {
        meal_name: newBooking.hotel.meal_name,
        meal_id: newBooking.hotel.meal_id,
        meal_plan: newBooking.hotel.meal_name,
        nights: newBooking.hotel.nights,
        rooms_count: newBooking.hotel.rooms_count,
        adult_count: newBooking.hotel.adult_count,
        child_count: newBooking.hotel.child_count,
        rooms: newBooking.hotel.rooms?.map((room, index) => ({
          id: room.id || index,
          name: room.name,
          bed_type: room.bed_type,
          count: room.count,
          adult: room.adult,
          child: room.child,
          amount: room.amount,
          amount_provider: room.amountProvider || room.prices?.[0]?.amountProvider,
          product_hotel_room_id: room.id || room.room_id,
          prices: room.prices?.map(price => ({
            date: price.date,
            amount: price.amount,
            amountProvider: price.amountProvider
          })),
          is_obsolete: room.is_obsolete || false
        })),
      } : null,

      // Passengers
      passengers: newBooking.passengers?.map(p => ({
        title: p.title,
        type: p.type,
        gender: p.gender,
        last_name: p.lastName,
        first_name: p.firstName,
        is_holder: `${p.firstName} ${p.lastName}` === newBooking.holder
      })),

      // Policies and conditions
      cancellation_policies: newBooking.cancellationPolicies?.map(cp => ({
        date: cp.date,
        amount: cp.amount,
        amountProvider: cp.amountProvider
      })),

      policies: newBooking.rateComment?.map(rc => ({
        value: rc.value
      })),

      // Additional data
      comments: newBooking.comments || [],
      extra_includes: newBooking.extraIncludes?.map(ei => ({
        value: ei.value
      })) || [],

      extra_excludes: newBooking.extraExclude?.map(ee => ({
        value: ee.value
      })) || []
    };
  };

  useEffect(() => {
    fetchBookingDetail();
  }, [id]);

  const handleVoucher = () => {
    navigate(`/voucher/${id}`);
  };

  const handleEdit = () => {
    navigate(`/voucher/edit/${id}`);
  };

  const handleCancelBooking = async () => {
    const result = await Swal.fire({
      title: t('confirmCancelBooking'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: t('Confirm'),
      cancelButtonText: t('exit')
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      setCancelling(true);
      const response = await api.post(`${booking_url}/${id}/cancel`);

      // Mettre à jour l'état local
      setBooking(prev => ({
        ...prev,
        status: 'cancelled',
        cancelled_at: new Date().toISOString()
      }));

      toast.success(t('bookingCancelledSuccess'));
    } catch (error) {
      console.error('Erreur lors de l\'annulation:', error);
      toast.error(t('cancelBookingError'));
    } finally {
      setCancelling(false);
    }
  };

  const exportToExcel = () => {
    if (!booking) return;
    setExporting(true);
    try {
      const exportData = [];

      // Basic booking info
      exportData.push(
        [t('category'), t('detail'), t('value')],
        [t('bookingInformation'), t('number'), booking.booking_number],
        ['', t('status'), t(booking.status)],
        ['', t('paymentStatus'), t(booking.payment_status)],
        ['', t('serviceType'), t(booking.service)],
        ['', t('createdDate'), new Date(booking.created_at).toLocaleString()],
        ['', t('cancelledDate'), booking.cancelled_at ? new Date(booking.cancelled_at).toLocaleString() : t('notAvailable')],
        ['', '', ''],

        // Dates
        [t('dates'), t('checkin'), booking.checkin ? new Date(booking.checkin).toLocaleDateString() : t('notAvailable')],
        ['', t('checkout'), booking.checkout ? new Date(booking.checkout).toLocaleDateString() : t('notAvailable')],
        ['', t('duration'), booking.duration ? `${booking.duration} ${t('days')}` : t('notAvailable')],
        ['', '', ''],

        // Financial
        [t('financialInformation'), t('totalAmount'), `${booking.amount_total || '0'} ${booking.currency || t('notAvailable')}`],
        ['', t('providerAmount'), `${booking.provider_amount_total || '0'} ${booking.provider_currency || t('notAvailable')}`],
        ['', t('paymentDeadline'), booking.payment_date_limit_at ? new Date(booking.payment_date_limit_at).toLocaleString() : t('notAvailable')],
        ['', t('cancellationDeadline'), booking.date_limit_cancellation ? new Date(booking.date_limit_cancellation).toLocaleString() : t('notAvailable')],
        ['', '', '']
      );

      // Customer information
      if (booking.customer) {
        exportData.push(
          [t('customerInformation'), t('id'), booking.customer.id],
          ['', t('name'), booking.customer.name],
          ['', t('phone'), booking.customer.phone],
          ['', t('website'), booking.customer.website],
          ['', t('address'), booking.customer.address],
          ['', t('language'), booking.customer.language],
          ['', t('balance'), booking.customer.balance],
          ['', '', '']
        );
      }

      // Provider information
      if (booking.provider) {
        exportData.push(
          [t('providerInformation'), t('id'), booking.provider.id],
          ['', t('name'), booking.provider.name],
          ['', t('email'), booking.provider.email],
          ['', t('phone'), booking.provider.phone],
          ['', t('website'), booking.provider.website],
          ['', t('address'), booking.provider.address],
          ['', t('currency'), booking.provider.currency],
          ['', t('status'), booking.provider.status],
          ['', '', '']
        );
      }

      // Product information
      if (booking.product) {
        exportData.push(
          [t('productInformation'), t('id'), booking.product.id],
          ['', t('name'), booking.product.name],
          ['', t('phone'), booking.product.phone],
          ['', t('address'), booking.product.address],
          ['', t('status'), booking.product.status],
          ['', t('country'), booking.product.country?.name],
          ['', t('city'), booking.product.city?.name],
          ['', '', '']
        );
      }

      // Passengers
      if (booking.passengers && booking.passengers.length > 0) {
        exportData.push([t('passengers'), '', '']);
        booking.passengers.forEach((passenger, index) => {
          exportData.push(
            [`${t('passenger')} ${index + 1}`, t('fullName'), `${passenger.first_name} ${passenger.last_name}`],
            ['', t('type'), t(passenger.type?.toLowerCase())],
            ['', t('title'), passenger.title],
            ['', t('gender'), passenger.gender === 'M' ? t('male') : passenger.gender === 'F' ? t('female') : t('notAvailable')],
            ['', '', '']
          );
        });
      }

      // Flight specific
      if (booking.service === 'flight' && booking.booking_flight) {
        const flight = booking.booking_flight;
        exportData.push([t('flightInformation'), '', '']);

        exportData.push(
          ['', t('tripType'), flight.trip],
          ['', t('airline'), flight.airline?.name || flight.airline?.code],
          ['', t('adults'), flight.adult],
          ['', t('children'), flight.child],
          ['', t('infants'), flight.infant],
          ['', t('totalPassengers'), (flight.adult || 0) + (flight.child || 0) + (flight.infant || 0)],
          ['', '', '']
        );

        if (flight.itineraries && flight.itineraries.length > 0) {
          exportData.push([t('itineraries'), '', '']);
          flight.itineraries.forEach((itinerary, index) => {
            exportData.push(
              [`${t('itinerary')} ${index + 1}`, t('departure'), `${itinerary.departure?.code} - ${itinerary.departure?.name}`],
              ['', t('arrival'), `${itinerary.arrival?.code} - ${itinerary.arrival?.name}`],
              ['', t('duration'), itinerary.duration],
              ['', '', '']
            );

            if (itinerary.segments && itinerary.segments.length > 0) {
              itinerary.segments.forEach((segment, segIndex) => {
                exportData.push(
                  [`${t('segment')} ${segIndex + 1}`, t('flightNumber'), segment.flightNumber],
                  ['', t('airline'), segment.airline?.name],
                  ['', t('departureTime'), segment.departure?.date ? new Date(segment.departure.date).toLocaleString() : t('notAvailable')],
                  ['', t('arrivalTime'), segment.arrival?.date ? new Date(segment.arrival.date).toLocaleString() : t('notAvailable')],
                  ['', '', '']
                );
              });
            }
          });
        }
      }

      // Cancellation policies
      if (cancellationPolicies && cancellationPolicies.length > 0) {
        exportData.push([t('cancellationPolicy'), '', '']);
        cancellationPolicies.forEach((policy, index) => {
          exportData.push(
            [`${t('policy')} ${index + 1}`, t('deadline'), new Date(policy.date).toLocaleDateString()],
            ['', t('customerAmount'), `${policy.amount} ${booking.currency}`],
            ['', t('providerAmount'), `${policy.amountProvider} ${booking.provider_currency}`],
            ['', '', '']
          );
        });
      }

      // General policies
      if (policies && policies.length > 0) {
        exportData.push([t('generalPolicies'), '', '']);
        policies.forEach((policy, index) => {
          exportData.push([`${t('policy')} ${index + 1}`, '', policy.value || t('notAvailable')]);
        });
      }

      // Comments
      if (booking.comments && booking.comments.length > 0) {
        exportData.push([t('comments'), '', '']);
        booking.comments.forEach((comment, index) => {
          exportData.push([`${t('comment')} ${index + 1}`, '', comment.value || t('notAvailable')]);
        });
      }

      // Création du workbook
      const ws = XLSX.utils.aoa_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, t('bookingDetails'));

      // Définition de la largeur des colonnes
      const colWidths = [
        { wch: 30 },
        { wch: 25 },
        { wch: 50 }
      ];
      ws['!cols'] = colWidths;

      // Téléchargement
      const fileName = `${t('booking')}_${booking.booking_number}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);

    } catch (error) {
      console.error('Erreur lors de l\'export Excel:', error);
      toast.error(t('exportError'));
    } finally {
      setExporting(false);
    }
  };

  const styles = {
    container: {
      padding: '8px 20px',
      background: '#f9fafb',
      minHeight: '100vh',
      maxWidth: '1600px',
      margin: '0 auto'
    },
    backButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 16px',
      backgroundColor: 'white',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '600',
      color: '#4b5563',
      transition: 'all 0.2s ease',
      marginBottom: '8px',
      width: 'fit-content',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
    },
    loading: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '400px',
      fontSize: '18px',
      color: '#6b7280'
    },
    section: {
      background: 'white',
      borderRadius: '12px',
      padding: '20px',
      marginBottom: '10px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)',
      overflow: 'hidden'
    },
    sectionTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1f2937',
      margin: '0 0 20px 0',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    sectionTitleSmall: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#374151',
      margin: '0 0 16px 0',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    gridContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '20px'
    },
    card: {
      backgroundColor: '#fff',
      borderRadius: '10px',
      padding: '18px',
      border: '1px solid #e5e7eb',
      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
      height: 'fit-content'
    },
    amountCard: {
      background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
      border: '1px solid #ddd6fe',
      borderRadius: '8px',
      padding: '18px',
      textAlign: 'center',
      transition: 'all 0.2s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 6px rgba(124, 58, 237, 0.2)'
      }
    },
    amountCardGreen: {
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      border: '1px solid #bbf7d0',
      borderRadius: '8px',
      padding: '18px',
      textAlign: 'center',
      transition: 'all 0.2s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 6px rgba(16, 185, 129, 0.2)'
      }
    },
    infoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
      gap: '16px'
    },
    infoItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px'
    },
    infoLabel: {
      fontSize: '12px',
      color: '#6b7280',
      fontWeight: '500',
      textTransform: 'uppercase',
      letterSpacing: '0.3px'
    },
    infoValue: {
      fontSize: '14px',
      color: '#1f2937',
      fontWeight: '500',
      wordBreak: 'break-word'
    },
    statusBadge: (status) => ({
      padding: '6px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '600',
      color: 'white',
      textTransform: 'uppercase',
      background: status === 'confirmed' ? '#10b981' :
        status === 'cancelled' ? '#ef4444' :
          status === 'pending' ? '#f59e0b' : '#6b7280',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      maxWidth: '100px',
      whiteSpace: 'nowrap'
    }),
    paymentBadge: (status) => ({
      padding: '6px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '600',
      color: 'white',
      textTransform: 'uppercase',
      background: status === 'paid' ? '#10b981' : '#ef4444',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      maxWidth: '100px',
      whiteSpace: 'nowrap'
    }),
    primaryButton: {
      padding: '10px 18px',
      background: '#7c3aed',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontWeight: '500',
      fontSize: '14px',
      transition: 'all 0.2s ease',
      '&:hover': {
        background: '#6d28d9'
      },
      '&:disabled': {
        opacity: 0.6,
        cursor: 'not-allowed'
      }
    },
    secondaryButton: {
      padding: '10px 18px',
      background: '#10b981',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontWeight: '500',
      fontSize: '14px',
      transition: 'all 0.2s ease',
      '&:hover': {
        background: '#059669'
      }
    },
    dangerButton: {
      padding: '10px 18px',
      background: '#ef4444',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontWeight: '500',
      fontSize: '14px',
      transition: 'all 0.2s ease',
      '&:hover': {
        background: '#dc2626'
      }
    },
    outlineButton: {
      padding: '8px 14px',
      background: 'transparent',
      color: '#7c3aed',
      border: '1px solid #7c3aed',
      borderRadius: '6px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontWeight: '500',
      fontSize: '13px',
      transition: 'all 0.2s ease',
      '&:hover': {
        background: '#f5f3ff'
      }
    },
    coloredCard: {
      background: '#f5f3ff',
      border: '1px solid #ddd6fe',
      borderRadius: '8px',
      padding: '18px',
      marginBottom: '12px'
    },
    greenCard: {
      background: '#f0fdf4',
      border: '1px solid #bbf7d0',
      borderRadius: '8px',
      padding: '18px',
      marginBottom: '12px'
    },
    purpleCard: {
      background: '#faf5ff',
      border: '1px solid #e9d5ff',
      borderRadius: '8px',
      padding: '18px',
      marginBottom: '12px'
    },
    headerContainer: {
      background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '20px',
      border: '1px solid #ddd6fe'
    },
    bookingNumber: {
      fontSize: '28px',
      fontWeight: 'bold',
      margin: '0 0 8px 0',
      color: '#7c3aed'
    },
    bookingMeta: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      flexWrap: 'wrap',
      marginBottom: '20px'
    },
    headerActions: {
      display: 'flex',
      gap: '12px',
      flexWrap: 'wrap',
      justifyContent: 'flex-start'
    },

    headerVoucher: {
      display: 'flex',
      gap: '12px',
      flexWrap: 'wrap',
      justifyContent: 'flex-end'
    },
    headerBadge: {
      padding: '6px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '600',
      color: 'white',
      textTransform: 'uppercase'
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <button
          onClick={() => navigate('/bookings')}
          style={styles.backButton}
        >
          <SquareChevronLeft size={20} />
          {t('backToBookings')}
        </button>
        <div style={styles.loading}>{t('loading')}</div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div style={styles.container}>
        <button
          onClick={() => navigate('/bookings')}
          style={styles.backButton}
        >
          <SquareChevronLeft size={20} />
          {t('backToBookings')}
        </button>
        <div style={styles.loading}>{t('bookingNotFound')}</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Bouton retour */}
      <button
        onClick={() => navigate('/bookings')}
        style={styles.backButton}
      >
        <SquareChevronLeft size={20} />
        {t('backToBookings')}
      </button>

      {/* En-tête principal */}
      <div style={styles.headerContainer}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '20px' }}>
          <div>
            <h1 style={styles.bookingNumber}>
              {t('booking')} #{booking.booking_number}
            </h1>
            <div style={styles.bookingMeta}>
              <span style={{
                ...styles.headerBadge,
                background: booking.status === 'confirmed' ? '#10b981' :
                  booking.status === 'cancelled' ? '#ef4444' :
                    booking.status === 'pending' ? '#f59e0b' : '#8b5cf6'
              }}>
                {t(booking.status)}
              </span>
              <span style={{
                ...styles.headerBadge,
                background: booking.payment_status === 'paid' ? '#10b981' : '#ef4444'
              }}>
                {t(booking.payment_status)}
              </span>
              <span style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '14px',
                color: '#374151'
              }}>
                <Calendar size={16} />
                {new Date(booking.created_at).toLocaleDateString(lang, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
          </div>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px',
            paddingTop: '0px',
            width: '100%',
            borderTop: '1px solid rgba(255,255,255,0.1)'
          }}>
            <div style={styles.headerActions}>
              <button
                style={styles.secondaryButton}
                onClick={exportToExcel}
                disabled={exporting}

                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-6px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {exporting ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTopColor: 'white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    {t('exporting')}
                  </>
                ) : (
                  <>
                    <FileSpreadsheet size={16} />
                    {t('exportToExcel')}
                  </>
                )}
              </button>

              {booking.status !== 'cancelled' && (
                <button
                  style={styles.dangerButton}
                  onClick={handleCancelBooking}
                  disabled={cancelling}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-6px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {cancelling ? (
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTopColor: 'white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                  ) : (
                    <XCircle size={16} />
                  )}
                  {cancelling ? t('cancelling') : t('cancelBooking')}
                </button>
              )}
            </div>

            <div style={styles.headerVoucher}>
              <button style={{
                ...styles.primaryButton,
              }} onClick={handleEdit}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-6px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <Download size={16} />
                {t('editVoucher')}
              </button>
              <button style={{
                ...styles.outlineButton,
              }} onClick={handleVoucher}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-6px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <Download size={16} />
                {t('viewVoucher')}
              </button>
            </div>

          </div>

        </div>
      </div>

      {/* DEUX COLONNES PRINCIPALES */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '20px',
        marginBottom: '20px'
      }}>
        {/* Colonne gauche - Informations générales */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Booking History */}
          <div style={styles.card}>
            <h2 style={styles.sectionTitle}>
              <Calendar size={16} color="#7c3aed" />
              {t('bookingHistory')}
            </h2>
            <BookingHistory booking={booking} t={t} lang={lang} styles={styles} />
          </div>

          {/* Booking Information */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <Building2 size={20} color="#7c3aed" />
              {t('bookingInformation')}
            </h2>
            <BookingInformation booking={booking} t={t} styles={styles} />
          </div>

          {/* Product Information */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <Package size={20} color="#7c3aed" />
              {t('productInformation')}
            </h2>
            <ProductInformation booking={booking} t={t} styles={styles} />
          </div>
        </div>

        {/* Colonne droite - Finance & Contacts */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Financial Information */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <DollarSign size={20} color="#7c3aed" />
              {t('financialInformation')}
            </h2>

            {/* Montants */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '16px',
                marginBottom: '16px'
              }}>
                <div style={styles.amountCard}>
                  <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', marginBottom: '4px' }}>
                    {t('totalAmount')}
                  </div>
                  <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#7c3aed' }}>
                    {booking.amount_total || '0'} {booking.currency || 'N/A'}
                  </div>
                </div>

                <div style={styles.amountCardGreen}>
                  <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', marginBottom: '4px' }}>
                    {t('providerAmount')}
                  </div>
                  <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#10b981' }}>
                    {booking.provider_amount_total || '0'} {booking.provider_currency || 'N/A'}
                  </div>
                </div>
              </div>
            </div>
            <FinancialInformation booking={booking} t={t} styles={styles} />
          </div>

          {/* Customer & Provider Information */}
          <div style={styles.section}>
            <ClientSection booking={booking} t={t} styles={styles} />
          </div>
          <div style={styles.section}>
            <ProviderSection booking={booking} t={t} styles={styles} />
          </div>
        </div>
      </div>

      {/* Service Specific Section */}
      <div style={{ marginTop: '10px' }}>
        <ServiceSpecificSection booking={booking} t={t} styles={styles} />
      </div>

      {/* Passenger Information */}
      <div style={{ ...styles.section, marginTop: '10px' }}>
        <h2 style={styles.sectionTitle}>
          <User size={20} color="#7c3aed" />
          {t('passengerInformation')}
          <span style={{ fontSize: '14px', color: '#6b7280', marginLeft: '8px' }}>
            ({booking.passengers?.length || 0})
          </span>
        </h2>
        <PassengersInformation booking={booking} t={t} styles={styles} />
      </div>

      {/* Terms & Conditions */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>
          <Shield size={20} color="#7c3aed" />
          {t('generalConditions')}
        </h2>
        <TermsConditions
          booking={booking}
          t={t}
          styles={styles}
          cancellationPolicies={cancellationPolicies}
          policies={policies}
        />
      </div>
    </div>
  );
};

// Composants auxiliaires

const BookingHistory = ({ booking, t, lang, styles }) => {
  const events = [
    { date: booking.created_at, label: t('bookingConfirmed'), icon: CheckCircle, color: '#10b981' },
    ...((booking.status && booking.cancelled_at) ? [{ date: booking.cancelled_at, label: t('bookingCancelled'), icon: XCircle, color: '#ef4444' }] : [])
  ];

  return (
    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
      {events.map((event, index) => (
        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
          <event.icon size={16} color={event.color} />
          <div>
            <span style={{ fontSize: '13px', fontWeight: '600' }}>{event.label}</span>
            <span style={{ fontSize: '12px', color: '#6b7280', margin: '4px' }}>
              {new Date(event.date).toLocaleDateString()}
            </span>
          </div>
          {index < events.length - 1 && <span style={{ color: '#d1d5db' }}><ArrowBigRight size={16} color="#7c3aed" style={{ marginLeft: '3px' }} /></span>}
        </div>
      ))}
    </div>
  );
};

const BookingInformation = ({ booking, t, styles }) => {
  const getServiceIcon = () => {
    switch (booking.service) {
      case 'hotel': return <Hotel size={16} color="#7c3aed" />;
      case 'activity': return <Mountain size={16} color="#7c3aed" />;
      case 'flight': return <Plane size={16} color="#7c3aed" />;
      case 'transfer': return <Car size={16} color="#7c3aed" />;
      default: return <Briefcase size={16} color="#7c3aed" />;
    }
  };

  return (
    <div style={styles.infoGrid}>
      <div style={styles.infoItem}>
        <span style={styles.infoLabel}>{t('serviceType')}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {getServiceIcon()}
          <span style={styles.infoValue}>{t(booking.service)}</span>
        </div>
      </div>

      <div style={styles.infoItem}>
        <span style={styles.infoLabel}>{t('bookingNumber')}</span>
        <span style={styles.infoValue}>{booking.booking_number}</span>
      </div>

      <div style={styles.infoItem}>
        <span style={styles.infoLabel}>{t('status')}</span>
        <span style={styles.statusBadge(booking.status)}>
          {t(booking.status)}
        </span>
      </div>

      <div style={styles.infoItem}>
        <span style={styles.infoLabel}>{t('paymentStatus')}</span>
        <span style={styles.paymentBadge(booking.payment_status)}>
          {t(booking.payment_status)}
        </span>
      </div>

      <div style={styles.infoItem}>
        <span style={styles.infoLabel}>{t('checkin')}</span>
        <span style={styles.infoValue}>
          {booking.checkin ? new Date(booking.checkin).toLocaleDateString() : t('notAvailable')}
        </span>
      </div>

      <div style={styles.infoItem}>
        <span style={styles.infoLabel}>{t('checkout')}</span>
        <span style={styles.infoValue}>
          {booking.checkout ? new Date(booking.checkout).toLocaleDateString() : t('notAvailable')}
        </span>
      </div>

      <div style={styles.infoItem}>
        <span style={styles.infoLabel}>{t('duration')}</span>
        <span style={styles.infoValue}>
          {`${booking.duration} ${t('days')}`}
        </span>
      </div>

      <div style={styles.infoItem}>
        <span style={styles.infoLabel}>{t('createdAt')}</span>
        <span style={styles.infoValue}>
          {booking.created_at ? new Date(booking.created_at).toLocaleString() : t('notAvailable')}
        </span>
      </div>


      <div style={styles.infoItem}>
        <span style={styles.infoLabel}>{t('cancelled_at')}</span>
        <span style={styles.infoValue}>
          {booking.cancelled_at ? booking.cancelled_at : t('notAvailable')}
        </span>
      </div>

      <div style={styles.infoItem}>
        <span style={styles.infoLabel}>{t('paymentDeadline')}</span>
        <span style={styles.infoValue}>
          {booking.payment_date_limit_at ? new Date(booking.payment_date_limit_at).toLocaleString() : t('notAvailable')}
        </span>
      </div>

      <div style={styles.infoItem}>
        <span style={styles.infoLabel}>{t('cancellationDeadline')}</span>
        <span style={styles.infoValue}>
          {booking.date_limit_cancellation ? new Date(booking.date_limit_cancellation).toLocaleString() : t('notAvailable')}
        </span>
      </div>

      <div style={styles.infoItem}>
        <span style={styles.infoLabel}>{t('holder')}</span>
        <span style={styles.infoValue}>{booking.holder || t('notAvailable')}</span>
      </div>
    </div>
  );
};

const ProductInformation = ({ booking, t, styles }) => {
  const navigate = useNavigate();
  if (!booking.product) {
    return <div style={{ color: '#6b7280', padding: '20px', textAlign: 'center' }}>{t('noProductInformation')}</div>;
  }

  return (
    <div
      style={{
        ...styles.purpleCard,
        cursor: 'pointer',
        position: 'relative',
        transition: 'all 0.2s ease',
        border: '1px solid #ddd6fe'
      }}
      onClick={() => navigate(`/products/${booking.product.id}`)}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(124, 58, 237, 0.15)';
        e.currentTarget.style.borderColor = '#7c3aed';
        e.currentTarget.style.background = 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)';
        e.currentTarget.style.borderColor = '#ddd6fe';
        e.currentTarget.style.background = '#f5f3ff';
      }}
    >
      {/* Indicateur de clic en haut à droite */}
      <div style={{
        position: 'absolute',
        top: '12px',
        right: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '11px',
        color: '#7c3aed',
        opacity: 0.8,
        transition: 'opacity 0.2s ease'
      }}>
        <span>{t('viewDetails')}</span>
        <ArrowBigRight size={12} />
      </div>

      {/* Badge "Customer" en haut à gauche */}
      <div style={{
        position: 'absolute',
        top: '12px',
        left: '12px',
        background: '#7c3aed',
        color: 'white',
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '11px',
        fontWeight: '600',
        letterSpacing: '0.5px'
      }}>
        {t('product')}
      </div>

      {/* Contenu principal avec padding ajusté pour l'espace des badges */}
      <div style={{
        marginTop: '24px',
        ...styles.infoGrid
      }}>
        <div style={styles.infoItem}>
          <span style={styles.infoLabel}>{t('productName')}</span>
          <span style={styles.infoValue}>{booking.product.name}</span>
        </div>

        <div style={styles.infoItem}>
          <span style={styles.infoLabel}>{t('phone')}</span>
          <span style={styles.infoValue}>{booking.product.phone || t('notAvailable')}</span>
        </div>

        <div style={styles.infoItem}>
          <span style={styles.infoLabel}>{t('address')}</span>
          <span style={styles.infoValue}>{booking.product.address || t('notAvailable')}</span>
        </div>

        <div style={styles.infoItem}>
          <span style={styles.infoLabel}>{t('status')}</span>
          <span style={{
            padding: '4px 8px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: '500',
            maxWidth: '100px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: booking.product.status === 'draft' ? '#fef3c7' : '#dcfce7',
            color: booking.product.status === 'draft' ? '#92400e' : '#166534'
          }}>
            {t(booking.product.status)}
          </span>
        </div>

        <div style={styles.infoItem}>
          <span style={styles.infoLabel}>{t('country')}</span>
          <span style={styles.infoValue}>
            {booking.product.country?.name ? `${booking.product.country.name} (${booking.product.country.id})` : t('notAvailable')}
          </span>
        </div>

        <div style={styles.infoItem}>
          <span style={styles.infoLabel}>{t('city')}</span>
          <span style={styles.infoValue}>
            {booking.product.city?.name ? `${booking.product.city.name} (ID: ${booking.product.city.id})` : t('notAvailable')}
          </span>
        </div>
      </div>
    </div>
  );
};

const FinancialInformation = ({ booking, t, styles }) => (
  <div style={styles.infoGrid}>

    <div style={styles.infoItem}>
      <span style={styles.infoLabel}>{t('paymentDeadline')}</span>
      <span style={styles.infoValue}>
        {booking.payment_date_limit_at ? new Date(booking.payment_date_limit_at).toLocaleString() : t('notAvailable')}
      </span>
    </div>

    <div style={styles.infoItem}>
      <span style={styles.infoLabel}>{t('cancellationDeadline')}</span>
      <span style={styles.infoValue}>
        {booking.date_limit_cancellation ? new Date(booking.date_limit_cancellation).toLocaleString() : t('notAvailable')}
      </span>
    </div>
  </div>
);

const ClientSection = ({ booking, t, styles }) => {
  const navigate = useNavigate();
  return (
    <div style={{ display: 'column', gridTemplateColumns: '1fr', gap: '20px' }}>
      {/* Customer Information */}
      <div style={{ marginBottom: '20px' }}>
        <h3 style={styles.sectionTitle}>
          <User size={20} color="#7c3aed" />
          {t('customerInformation')}
        </h3>
        {booking.customer ? (
          <div
            style={{
              ...styles.coloredCard,
              cursor: 'pointer',
              position: 'relative',
              transition: 'all 0.2s ease',
              border: '1px solid #ddd6fe'
            }}
            onClick={() => navigate(`/customers/${booking.customer.id}`)}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(124, 58, 237, 0.15)';
              e.currentTarget.style.borderColor = '#7c3aed';
              e.currentTarget.style.background = 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)';
              e.currentTarget.style.borderColor = '#ddd6fe';
              e.currentTarget.style.background = '#f5f3ff';
            }}
          >
            {/* Indicateur de clic en haut à droite */}
            <div style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '11px',
              color: '#7c3aed',
              opacity: 0.8,
              transition: 'opacity 0.2s ease'
            }}>
              <span>{t('viewDetails')}</span>
              <ArrowBigRight size={12} />
            </div>

            {/* Badge "Customer" en haut à gauche */}
            <div style={{
              position: 'absolute',
              top: '12px',
              left: '12px',
              background: '#7c3aed',
              color: 'white',
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '11px',
              fontWeight: '600',
              letterSpacing: '0.5px'
            }}>
              {t('customer')}
            </div>

            {/* Contenu principal avec padding ajusté pour l'espace des badges */}
            <div style={{
              marginTop: '24px',
              ...styles.infoGrid
            }}>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>{t('name')}</span>
                <span style={{
                  ...styles.infoValue,
                  fontWeight: '600',
                  color: '#7c3aed'
                }}>
                  {booking.customer.name || t('notAvailable')}
                </span>
              </div>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>{t('phone')}</span>
                <span style={styles.infoValue}>{booking.customer.phone || t('notAvailable')}</span>
              </div>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>{t('website')}</span>
                <span style={styles.infoValue}>{booking.customer.website || t('notAvailable')}</span>
              </div>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>{t('address')}</span>
                <span style={styles.infoValue}>{booking.customer.address || t('notAvailable')}</span>
              </div>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>{t('balance')}</span>
                <span style={styles.infoValue}>
                  {booking.customer.balance || '0'}
                </span>
              </div>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>{t('deposit')}</span>
                <span style={styles.infoValue}>
                  {booking.customer.deposit || '0'}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ color: '#6b7280', padding: '12px' }}>{t('clientInfoNotAvailable')}</div>
        )}
      </div>
    </div>
  );
};


const ProviderSection = ({ booking, t, styles }) => {
  const navigate = useNavigate();
  return (
    <div style={{ display: 'column', gridTemplateColumns: '1fr', gap: '20px' }}>
      <h3 style={styles.sectionTitle}>
        <Building2 size={20} color="#7c3aed" />
        {t('providerInformation')}
      </h3>
      {booking.provider ? (
        <div
          style={{
            ...styles.greenCard,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            position: 'relative'
          }}
          onClick={() => navigate(`/suppliers/${booking.provider.id}`)}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.15)';
            e.currentTarget.style.borderColor = '#10b981';
            e.currentTarget.style.background = 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)';
            e.currentTarget.style.borderColor = '#bbf7d0';
            e.currentTarget.style.background = '#f0fdf4';
          }}
        >
          <div style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '11px',
            color: '#10b981',
            opacity: 0.8
          }}>
            <span>{t('viewDetails')}</span>
            <ArrowBigRight size={12} />
          </div>

          <div style={{
            position: 'absolute',
            top: '12px',
            left: '12px',
            background: '#10b981',
            color: 'white',
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: '600',
            letterSpacing: '0.5px'
          }}>
            {t('provider')}
          </div>

          <div style={{
            marginTop: '24px',
            ...styles.infoGrid
          }}>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>{t('name')}</span>
              <span style={styles.infoValue}>{booking.provider.name || t('notAvailable')}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>{t('email')}</span>
              <span style={styles.infoValue}>{booking.provider.email || t('notAvailable')}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>{t('phone')}</span>
              <span style={styles.infoValue}>{booking.provider.phone || t('notAvailable')}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>{t('website')}</span>
              <span style={styles.infoValue}>{booking.provider.website || t('notAvailable')}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>{t('address')}</span>
              <span style={styles.infoValue}>{booking.provider.address || t('notAvailable')}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>{t('status')}</span>
              <span style={{
                padding: '4px 8px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '500',
                maxWidth: '100px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: booking.provider.status === 'enabled' ? '#dcfce7' : '#fef3c7',
                color: booking.provider.status === 'enabled' ? '#166534' : '#92400e'
              }}>
                {t(booking.provider.status)}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ color: '#6b7280', padding: '12px' }}>{t('providerInfoNotAvailable')}</div>
      )}
    </div>
  );
};

const PassengersInformation = ({ booking, t, styles }) => {
  if (!booking.passengers || booking.passengers.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '30px', color: '#6b7280' }}>
        <User size={32} color="#9ca3af" />
        <p style={{ marginTop: '12px', fontSize: '14px' }}>{t('noPassengers')}</p>
      </div>
    );
  }

  // Fonction simple pour identifier le holder
  const isPassengerHolder = (passenger) => {
    if (!booking.holder) return false;

    // Extraire le nom et prénom du holder (en ignorant le titre)
    const holderWithoutTitle = booking.holder.replace(/^(MR|MRS|MS|DR|PROF)\s+/i, '');
    const holderParts = holderWithoutTitle.split(' ');

    // Prendre le dernier mot comme nom, le reste comme prénom
    const holderLastName = holderParts.length > 0 ? holderParts[holderParts.length - 1] : '';
    const holderFirstName = holderParts.length > 1 ? holderParts.slice(0, -1).join(' ') : holderParts[0] || '';

    // Comparer insensible à la casse
    return (passenger.last_name.toLowerCase() === holderLastName.toLowerCase() &&
      passenger.first_name.toLowerCase() === holderFirstName.toLowerCase()) ||
      (passenger.first_name.toLowerCase() === holderLastName.toLowerCase() &&
        passenger.last_name.toLowerCase() === holderFirstName.toLowerCase());
  };

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
      gap: '20px'
    }}>
      {booking.passengers.map((passenger, index) => {
        const isHolder = isPassengerHolder(passenger);

        return (
          <div
            key={index}
            style={{
              ...styles.coloredCard,
              border: isHolder ? '2px solid #7c3aed' : '1px solid #ddd6fe',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* En-tête du passager */}
            <div style={{
              marginBottom: '16px',
              borderBottom: '1px solid rgba(124, 58, 237, 0.1)',
              paddingBottom: '12px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: isHolder
                    ? 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)'
                    : '#f5f3ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: isHolder ? '2px solid #7c3aed' : '1px solid #ddd6fe'
                }}>
                  <User size={20} color={isHolder ? '#7c3aed' : '#8b5cf6'} />
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    margin: 0,
                    fontSize: '16px',
                    fontWeight: '600',
                    color: isHolder ? '#7c3aed' : '#1f2937',
                    lineHeight: '1.3'
                  }}>
                    {passenger.first_name} {passenger.last_name}
                  </h3>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginTop: '4px'
                  }}>
                    <span style={{
                      fontSize: '12px',
                      color: '#6b7280',
                      background: 'rgba(124, 58, 237, 0.08)',
                      padding: '2px 8px',
                      borderRadius: '4px'
                    }}>
                      {passenger.title}
                    </span>
                    <span style={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: passenger.type === 'ADT' ? '#10b981' :
                        passenger.type === 'CHD' ? '#f59e0b' : '#7c3aed',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      background: passenger.type === 'ADT' ? 'rgba(16, 185, 129, 0.1)' :
                        passenger.type === 'CHD' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(124, 58, 237, 0.1)'
                    }}>
                      {t(passenger.type?.toLowerCase())}
                    </span>
                  </div>
                </div>

                {isHolder && (
                  <div style={{
                    background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                    color: 'white',
                    padding: '4px 10px',
                    borderRadius: '20px',
                    fontSize: '11px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    boxShadow: '0 2px 4px rgba(124, 58, 237, 0.3)',
                    zIndex: 1
                  }}>

                    {t('holder')}
                  </div>
                )}

              </div>
            </div>

            {/* Informations détaillées */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '12px'
            }}>
              <div style={{
                padding: '8px',
                background: '#f9fafb',
                borderRadius: '6px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                  {t('gender')}
                </div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: passenger.gender === 'M' ? '#3b82f6' : '#ec4899',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  {passenger.gender === 'M' ? <Mars size={15} /> : passenger.gender === 'F' ? <Venus size={15} /> : '?'}
                  {passenger.gender === 'M' ? t('male') :
                    passenger.gender === 'F' ? t('female') :
                      t('notAvailable')}
                </div>
              </div>

              <div style={{
                padding: '8px',
                background: '#f9fafb',
                borderRadius: '6px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                  {t('type')}
                </div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: passenger.type === 'ADT' ? '#10b981' :
                    passenger.type === 'CHD' ? '#f59e0b' : '#7c3aed'
                }}>
                  {t(passenger.type?.toLowerCase())}
                </div>
              </div>
            </div>

          </div>
        );
      })}
    </div>
  );
};

const TermsConditions = ({ booking, t, styles, cancellationPolicies, policies, loadingPolicies }) => {
  const [activeTab, setActiveTab] = useState('all');

  const hasContent = policies.length > 0 ||
    cancellationPolicies.length > 0 ||
    booking.extra_excludes?.length > 0 ||
    booking.extra_includes?.length > 0;

  if (!hasContent && !loadingPolicies) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
        <Shield size={48} color="#9ca3af" />
        <p style={{ marginTop: '16px' }}>{t('noConditions')}</p>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'included':
        return (
          <div>
            {booking.extra_includes?.length > 0 ? (
              <div style={styles.greenCard}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: '0 0 12px 0' }}>
                  {t('includedServices')}
                </h3>
                {booking.extra_includes.map((include, index) => (
                  <div key={include.id} style={{
                    marginBottom: '8px',
                    padding: '8px',
                    background: 'white',
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                      <CheckCircle size={14} color="#10b981" style={{ flexShrink: 0, marginTop: '2px' }} />
                      <span style={styles.infoValue}>{include.value || 'N/A'}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                {t('noIncludedServices')}
              </div>
            )}
          </div>
        );

      case 'excluded':
        return (
          <div>
            {booking.extra_excludes?.length > 0 ? (
              <div style={styles.coloredCard}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: '0 0 12px 0' }}>
                  {t('excludedServices')}
                </h3>
                {booking.extra_excludes.map((exclude, index) => (
                  <div key={exclude.id} style={{
                    marginBottom: '8px',
                    padding: '8px',
                    background: 'white',
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                      <XCircle size={14} color="#ef4444" style={{ flexShrink: 0, marginTop: '2px' }} />
                      <span style={styles.infoValue}>{exclude.value || 'N/A'}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                {t('noExcludedServices')}
              </div>
            )}
          </div>
        );

      case 'cancellation':
        return (
          <div>
            {cancellationPolicies.length > 0 ? (
              <div style={styles.coloredCard}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: '0 0 12px 0' }}>
                  {t('cancellationPolicy')} ({cancellationPolicies.length})
                </h3>
                {cancellationPolicies.map((policy) => (
                  <div key={policy.id} style={{
                    marginBottom: '16px',
                    padding: '12px',
                    background: 'white',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                      <div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                          {t('deadline')}
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: '500', color: '#ef4444' }}>
                          {new Date(policy.date).toLocaleDateString()}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                          {t('providerAmount')}
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: '500', color: '#ef4444' }}>
                          {policy.amountProvider} {booking.provider_currency}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                          {t('customerAmount')}
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: '500', color: '#ef4444' }}>
                          {policy.amount} {booking.currency}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                {t('noCancellationPolicy')}
              </div>
            )}
          </div>
        );

      case 'policies':
        return (
          <div>
            {policies.length > 0 ? (
              <div style={styles.purpleCard}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: '0 0 12px 0' }}>
                  {t('generalPolicies')} ({policies.length})
                </h3>
                {policies.map((policy) => (
                  <div key={policy.id} style={{
                    marginBottom: '12px',
                    padding: '12px',
                    background: 'white',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <span style={styles.infoValue}>{policy.value || 'N/A'}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                {t('noGeneralPolicies')}
              </div>
            )}
          </div>
        );

      default: // 'all'
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Included Services */}
            {booking.extra_includes?.length > 0 && (
              <div style={styles.greenCard}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: '0 0 12px 0' }}>
                  {t('includedServices')} ({booking.extra_includes.length})
                </h3>
                {booking.extra_includes.map((include) => (
                  <div key={include.id} style={{
                    marginBottom: '8px',
                    padding: '8px',
                    background: 'white',
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                      <CheckCircle size={14} color="#10b981" style={{ flexShrink: 0, marginTop: '2px' }} />
                      <span style={styles.infoValue}>{include.value || 'N/A'}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Excluded Services */}
            {booking.extra_excludes?.length > 0 && (
              <div style={styles.coloredCard}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: '0 0 12px 0' }}>
                  {t('excludedServices')} ({booking.extra_excludes.length})
                </h3>
                {booking.extra_excludes.map((exclude) => (
                  <div key={exclude.id} style={{
                    marginBottom: '8px',
                    padding: '8px',
                    background: 'white',
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                      <XCircle size={14} color="#ef4444" style={{ flexShrink: 0, marginTop: '2px' }} />
                      <span style={styles.infoValue}>{exclude.value || 'N/A'}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Cancellation Policies */}
            {cancellationPolicies.length > 0 && (
              <div style={styles.coloredCard}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: '0 0 12px 0' }}>
                  {t('cancellationPolicy')} ({cancellationPolicies.length})
                </h3>
                {cancellationPolicies.map((policy, index) => (
                  <div key={index} style={{
                    marginBottom: '16px',
                    padding: '12px',
                    background: 'white',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                      <div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                          {t('deadline')}
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: '500', color: '#ef4444' }}>
                          {new Date(policy.date).toLocaleDateString()}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                          {t('providerAmount')}
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: '500', color: '#ef4444' }}>
                          {policy.amountProvider} {booking.provider_currency}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                          {t('customerAmount')}
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: '500', color: '#ef4444' }}>
                          {policy.amount} {booking.currency}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* General Policies */}
            {policies.length > 0 && (
              <div style={styles.purpleCard}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: '0 0 12px 0' }}>
                  {t('generalPolicies')} ({policies.length})
                </h3>
                {policies.map((policy) => (
                  <div key={policy.id} style={{
                    marginBottom: '12px',
                    padding: '12px',
                    background: 'white',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <span style={styles.infoValue}>{policy.value || 'N/A'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
    }
  };

  return (
    <div>
      {/* Onglets */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '24px',
        borderBottom: '1px solid #e5e7eb',
        paddingBottom: '12px',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={() => setActiveTab('all')}
          style={{
            padding: '10px 16px',
            background: activeTab === 'all' ? '#7c3aed' : 'transparent',
            color: activeTab === 'all' ? 'white' : '#6b7280',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <Shield size={16} />
          {t('allConditions')}
        </button>
        {booking.extra_includes?.length > 0 && (
          <button
            onClick={() => setActiveTab('included')}
            style={{
              padding: '10px 16px',
              background: activeTab === 'included' ? '#10b981' : 'transparent',
              color: activeTab === 'included' ? 'white' : '#6b7280',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <CheckCircle size={16} />
            {t('includedServices')} {booking.extra_includes?.length > 0 && `(${booking.extra_includes.length})`}
          </button>
        )}

        {booking.extra_excludes?.length > 0 && (

          <button
            onClick={() => setActiveTab('excluded')}
            style={{
              padding: '10px 16px',
              background: activeTab === 'excluded' ? '#ef4444' : 'transparent',
              color: activeTab === 'excluded' ? 'white' : '#6b7280',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <XCircle size={16} />
            {t('excludedServices')} {booking.extra_excludes?.length > 0 && `(${booking.extra_excludes.length})`}
          </button>
        )}

        {cancellationPolicies.length > 0 && (
          <button
            onClick={() => setActiveTab('cancellation')}
            style={{
              padding: '10px 16px',
              background: activeTab === 'cancellation' ? '#f59e0b' : 'transparent',
              color: activeTab === 'cancellation' ? 'white' : '#6b7280',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <XCircle size={16} />
            {t('cancellationPolicy')} ({cancellationPolicies.length})
          </button>
        )}

        {policies.length > 0 && (
          <button
            onClick={() => setActiveTab('policies')}
            style={{
              padding: '10px 16px',
              background: activeTab === 'policies' ? '#3b82f6' : 'transparent',
              color: activeTab === 'policies' ? 'white' : '#6b7280',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Shield size={16} />
            {t('generalPolicies')} ({policies.length})
          </button>
        )}
      </div>

      {loadingPolicies && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{
            display: 'inline-block',
            width: '32px',
            height: '32px',
            border: '3px solid #e5e7eb',
            borderTopColor: '#7c3aed',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{ marginTop: '12px', color: '#6b7280' }}>{t('loadingConditions')}</p>
        </div>
      )}

      {!loadingPolicies && renderTabContent()}
    </div>
  );
};

const ServiceSpecificSection = ({ booking, t, styles }) => {
  switch (booking.service) {
    case 'hotel':
      return <HotelSection booking={booking} t={t} styles={styles} />;
    case 'activity':
      return <ActivitySection booking={booking} t={t} styles={styles} />;
    case 'flight':
      return <FlightSection booking={booking} t={t} styles={styles} />;
    case 'transfer':
      return <TransferSection booking={booking} t={t} styles={styles} />;
    default:
      return null;
  }
};


export default BookingDetailPage;